import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Input, DatePickerButton, Button } from 'design-system-web';
import Dropdown from 'components/form/Dropdown';

import styles from './RecentActivityFilters.scss';

export type RecentActivitySortOption = {
  label: string;
  value: {
    key: string;
    dir: 'asc' | 'desc';
  };
};

export type RecentActivityFiltersState = {
  search: string;
  sortBy: RecentActivitySortOption['value'] | null;
  startDate: Date | null;
  endDate: Date | null;
};

type Props = {
  filters: RecentActivityFiltersState;
  onFiltersChange: (filters: RecentActivityFiltersState) => void;
};

const SORT_OPTIONS: RecentActivitySortOption[] = [
  {
    label: 'web.pages.portal.home.me.recentActivity.filters.sortBy.bookingOrderNewest',
    value: { key: '_created', dir: 'desc' },
  },
  {
    label: 'web.pages.portal.home.me.recentActivity.filters.sortBy.bookingOrderOldest',
    value: { key: '_created', dir: 'asc' },
  },
  {
    label: 'web.pages.portal.home.me.recentActivity.filters.sortBy.lastUpdatedNewest',
    value: { key: '_updated', dir: 'desc' },
  },
  {
    label: 'web.pages.portal.home.me.recentActivity.filters.sortBy.lastUpdatedOldest',
    value: { key: '_updated', dir: 'asc' },
  },
  {
    label: 'web.pages.portal.home.me.recentActivity.filters.sortBy.reservationDateNewest',
    value: { key: 'startDate', dir: 'desc' },
  },
  {
    label: 'web.pages.portal.home.me.recentActivity.filters.sortBy.reservationDateOldest',
    value: { key: 'startDate', dir: 'asc' },
  },
  {
    label: 'web.pages.portal.home.me.recentActivity.filters.sortBy.status',
    value: { key: 'status', dir: 'asc' },
  },
];

export default function RecentActivityFilters({ filters, onFiltersChange }: Props) {
  const { t } = useTranslation();

  const [localFilters, setLocalFilters] = useState<RecentActivityFiltersState>(filters);
  const hasSetDefaultSort = useRef(false);

  const sortOptions = useMemo(() => 
    SORT_OPTIONS.map(option => ({
      label: option.label,
      value: option.value,
    })), []
  );

  // Set the default sort option if no sort is currently selected
  useEffect(() => {
    if (filters.sortBy === null && sortOptions.length > 0 && !hasSetDefaultSort.current) {
      hasSetDefaultSort.current = true;
      onFiltersChange({ 
        search: filters.search,
        sortBy: sortOptions[0].value,
        startDate: filters.startDate,
        endDate: filters.endDate
      });
    }
  }, [filters.sortBy, filters.search, filters.startDate, filters.endDate, sortOptions, onFiltersChange]);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleSearchChange = (value: string) => {
    const newFilters = { ...localFilters, search: value };

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSortChange = (value: RecentActivitySortOption['value']) => {
    const newFilters = { ...localFilters, sortBy: value };

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleStartDateChange = (date: Date) => {
    const newFilters = { ...localFilters, startDate: date };
    
    // If end date is before the new start date, clear it
    if (newFilters.endDate && newFilters.endDate < date) {
      newFilters.endDate = null;
    }

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleEndDateChange = (date: Date) => {
    const newFilters = { ...localFilters, endDate: date };

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearStartDate = () => {
    const newFilters = { ...localFilters, startDate: null };

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearEndDate = () => {
    const newFilters = { ...localFilters, endDate: null };

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };


  const getCurrentSortValue = () => {
    return filters.sortBy;
  };

  return (
    <div className={styles.RecentActivityFilters} data-test="recent-activity-filters">
      <div className={styles.filtersRow}>
        {/* Search Input */}
        <div className={styles.searchContainer}>
          <Input
            onChange={handleSearchChange}
            icon="search"
            ariaLabel={t('web.pages.portal.home.me.recentActivity.filters.search')}
            fieldName="searchFilter"
            value={localFilters.search}
            placeholder={t('web.pages.portal.home.me.recentActivity.filters.search')}
            data-test="recent-activity-search"
          />
        </div>

        {/* Sort Dropdown */}
        <div className={styles.sortContainer}>
          <label className={styles.sortLabel} htmlFor="sortBy">
            {t('web.pages.portal.home.me.recentActivity.filters.sortBy')}
          </label>
          <Dropdown
            id="sortBy"
            data-test="sortBy"
            className={styles.sortDropdown}
            value={getCurrentSortValue()}
            items={sortOptions}
            onValueChange={handleSortChange}
            placeholder="web.pages.portal.home.me.recentActivity.filters.selectItem"
            doTranslation
          />
        </div>

        {/* Date Range */}
        <div className={styles.dateRangeContainer}>
          <div className={styles.dateField}>
            <div className={styles.dateFieldHeader}>
              {localFilters.startDate && (
                <Button
                  variant="text"
                  size="medium"
                  onClick={handleClearStartDate}
                  data-test="clear-start-date"
                  className={styles.clearButton}
                >
                  {t('web.pages.portal.home.me.recentActivity.filters.clear')}
                </Button>
              )}
            </div>
            <DatePickerButton
              value={localFilters.startDate}
              onChange={handleStartDateChange}
              placeholder="Start Date"
              dateInputLabel={t('Start date')}
              data-test="start-date-picker"
              className={styles.datePicker}
            />
          </div>
          
          <div className={styles.dateField}>
            <div className={styles.dateFieldHeader}>
              {localFilters.endDate && (
                <Button
                  variant="text"
                  size="medium"
                  onClick={handleClearEndDate}
                  data-test="clear-end-date"
                  className={styles.clearButton}
                >
                  {t('web.pages.portal.home.me.recentActivity.filters.clear')}
                </Button>
              )}
            </div>
            <DatePickerButton
              value={localFilters.endDate}
              onChange={handleEndDateChange}
              placeholder="End Date"
              dateInputLabel={t('End date')}
              data-test="end-date-picker"
              className={styles.datePicker}
              minDate={localFilters.startDate || undefined}
            />
          </div>

        </div>
      </div>
    </div>
  );
}
